import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { billService, Bill, BillInput, BillType, BillStatus, ResourceType, Payment, BillSearchFilters } from '../../services/billService';
import { studentService, Student } from '../../services/studentService';
import { teacherService, Teacher } from '../../services/teacherService';
import { adminService, Admin } from '../../services/adminService';
import { classeService, Classe } from '../../services/classeService';
import { useRights } from '../../hooks/useRights';
import { getErrorTranslation } from '../../utils/errorUtils';
import { useModal } from '../../context/ModalContext';
import { Pencil, Trash2, Eye } from 'lucide-react';
import '../../styles/index.css';
import './Bills.css';
import TableActions from '../../components/TableActions';

const Bills: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasRight } = useRights();
  const { showConfirm, showAlert } = useModal();

  // Get initial filters from URL params (for resource filtering)
  const resourceTypeParam = searchParams.get('resourceType') as ResourceType | null;
  const resourceIdParam = searchParams.get('resourceId');

  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingBill, setEditingBill] = useState<Bill | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [classes, setClasses] = useState<Classe[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [resourceSearchTerm, setResourceSearchTerm] = useState<string>('');
  const [viewingBill, setViewingBill] = useState<Bill | null>(null);
  const [editingPaymentIndex, setEditingPaymentIndex] = useState<number | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [newPayment, setNewPayment] = useState<Payment>({
    paymentDate: new Date().toISOString().split('T')[0],
    amount: 0,
    note: '',
  });

  const toLocalISOString = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
  };

  // Filters
  const [statusFilter, setStatusFilter] = useState<BillStatus | ''>('');
  const [billTypeFilter, setBillTypeFilter] = useState<BillType | ''>('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState<ResourceType | ''>(resourceTypeParam || '');
  const [resourceIdFilter, setResourceIdFilter] = useState<string>(resourceIdParam || ''); // Still needed for creation form, but maybe not for filter? 
  const [classFilter, setClassFilter] = useState<string>('');
  const [resourceNameFilter, setResourceNameFilter] = useState<string>('');


  // Date filters - default to 2 months ago to now
  const getDefaultFromDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 2);
    return date.toISOString().split('T')[0];
  };
  const getDefaultToDate = () => {
    return new Date().toISOString().split('T')[0];
  };
  const [fromDate, setFromDate] = useState<string>(getDefaultFromDate());
  const [toDate, setToDate] = useState<string>(getDefaultToDate());

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Form state
  const [formData, setFormData] = useState<BillInput>({
    amount: 0,
    resourceType: ResourceType.STUDENT,
    resourceId: resourceIdParam || '',
    payments: [],
    creationDatetime: new Date().toISOString().split('T')[0] + 'T00:00:00.000Z',
  });
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    loadBills(0, true);
    if (hasRight('bills:c') || hasRight('bills:u')) {
      loadResources();
      loadClasses();
    }
  }, []);



  useEffect(() => {
    if (selectedClassId && formData.resourceType === ResourceType.STUDENT) {
      loadClassStudents();
    } else {
      setClassStudents([]);
    }
  }, [selectedClassId, formData.resourceType]);

  const loadResources = async () => {
    try {
      const [studentsResponse, teachersData, adminsData] = await Promise.all([
        studentService.searchStudents({ page: 0, size: 1000 }),
        teacherService.getAllTeachers(),
        adminService.getAllAdmins(),
      ]);
      setStudents(studentsResponse.content);
      setTeachers(teachersData);
      setAdmins(adminsData);
    } catch (e: any) {
      console.error('Failed to load resources:', e);
    }
  };

  const loadClasses = async () => {
    try {
      const data = await classeService.getAllClasses();
      setClasses(data);
    } catch (e: any) {
      console.error('Failed to load classes:', e);
    }
  };

  const loadClassStudents = async () => {
    if (!selectedClassId) {
      setClassStudents([]);
      return;
    }
    try {
      const studentsResponse = await studentService.searchStudents({
        page: 0,
        size: 1000,
        filters: { classId: selectedClassId },
      });
      setClassStudents(studentsResponse.content);
    } catch (e: any) {
      console.error('Failed to load class students:', e);
      setClassStudents([]);
    }
  };

  const loadBills = async (page: number = 0, reset: boolean = false) => {
    try {
      if (reset) {
        setLoading(true);
        setBills([]);
      } else {
        setLoadingMore(true);
      }
      setError('');

      const filters: BillSearchFilters = {
        status: statusFilter || undefined,
        billType: billTypeFilter || undefined,
        resourceType: resourceTypeFilter || undefined,
        resourceId: resourceIdFilter || undefined,
        classId: classFilter || undefined,
        resourceName: resourceNameFilter || undefined,
      };

      // Add date filters (send as date strings yyyy-MM-dd)
      if (fromDate) {
        filters.fromDate = fromDate; // Already in yyyy-MM-dd format
      }
      if (toDate) {
        filters.toDate = toDate; // Already in yyyy-MM-dd format
      }

      const response = await billService.searchBills({
        page,
        size: 50,
        filters,
      });

      if (reset) {
        setBills(response.content);
      } else {
        setBills(prev => [...prev, ...response.content]);
      }

      setHasMore(response.content.length === 50 && response.totalElements > (page + 1) * 50);
      setCurrentPage(page);
    } catch (e: any) {
      setError(getErrorTranslation(e, t));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Update handleSearch to reset pagination and load
  const handleSearch = () => {
    setCurrentPage(0);
    setHasMore(true);
    setBills([]);
    loadBills(0, true);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setBillTypeFilter('');
    setResourceTypeFilter('');
    setResourceIdFilter(''); // Clear hidden URL param filter too
    setClassFilter('');
    setResourceNameFilter('');
    setFromDate(getDefaultFromDate());
    setToDate(getDefaultToDate());
    // Optionally trigger search or wait for user? User said "till user click search".
    // So just clear state.
  };


  // Infinite scroll handler
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadBills(currentPage + 1, false);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loadingMore, loading, currentPage]);

  const handleCreate = () => {
    setIsCreating(true);
    setIsBulkMode(false);
    setSelectedResourceIds([]);
    setSelectedClassId('');
    const now = new Date();
    setFormData({
      amount: 0,
      resourceType: resourceTypeFilter as ResourceType || ResourceType.STUDENT,
      resourceId: resourceIdFilter || '',
      payments: [],
      creationDatetime: now.toISOString().split('T')[0] + 'T00:00:00.000Z', // Default to today at midnight
    });
  };

  const handleEdit = async (bill: Bill) => {
    try {
      // Reload the bill to get the latest data including payments
      const fullBill = await billService.getBillById(bill.id);
      setEditingBill(fullBill);
      setFormData({
        amount: fullBill.amount,
        resourceType: fullBill.resourceType,
        resourceId: fullBill.resourceId,
        payments: fullBill.payments || [],
        // creationDatetime is not editable, so we don't include it
      });
    } catch (e: any) {
      setError(getErrorTranslation(e, t));
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingBill(null);
    setIsBulkMode(false);
    setSelectedResourceIds([]);
    setSelectedClassId('');
    setResourceSearchTerm('');
    const now = new Date();
    setFormData({
      amount: 0,
      resourceType: ResourceType.STUDENT,
      resourceId: '',
      payments: [],
      creationDatetime: now.toISOString().split('T')[0] + 'T00:00:00.000Z',
    });
  };

  const handleSave = async () => {
    try {
      if (editingBill) {
        await billService.updateBill(editingBill.id, formData);
      } else if (isBulkMode && selectedResourceIds.length > 0) {
        // Bulk create - creationDatetime will be set for each bill
        await billService.bulkCreateBills(
          formData.amount,
          formData.resourceType,
          selectedResourceIds,
          formData.creationDatetime
        );
      } else {
        // Single create
        await billService.createBill(formData);
      }
      await loadBills(0, true);
      handleCancel();
    } catch (e: any) {
      setError(getErrorTranslation(e, t));
    }
  };

  const handleDelete = (bill: Bill) => {
    showConfirm(t('common.delete'), t('bills.confirmDelete'), async () => {
      try {
        await billService.deleteBill(bill.id);
        await loadBills(0, true);
      } catch (e: any) {
        setError(getErrorTranslation(e, t));
        showAlert(t('common.error'), getErrorTranslation(e, t));
      }
    });
  };

  const handleAddPayment = async (billId: string, payment: Payment) => {
    try {
      const paymentToSend: Payment = {
        ...payment,
        paymentDate: new Date(payment.paymentDate).toISOString(),
      };

      if (editingPaymentIndex !== null) {
        await billService.updatePayment(billId, editingPaymentIndex, paymentToSend);
      } else {
        await billService.addPayment(billId, paymentToSend);
      }

      await loadBills(0, true);
      if (editingBill && editingBill.id === billId) {
        // Reload the bill being edited
        const updatedBill = await billService.getBillById(billId);
        setEditingBill(updatedBill);
        setFormData({
          amount: updatedBill.amount,
          resourceType: updatedBill.resourceType,
          resourceId: updatedBill.resourceId,
          payments: updatedBill.payments || [],
        });
      }
      if (viewingBill && viewingBill.id === billId) {
        const updatedBill = await billService.getBillById(billId);
        setViewingBill(updatedBill);
      }
      setPaymentModalOpen(false);
      setEditingPaymentIndex(null);
      setNewPayment({
        paymentDate: new Date().toISOString().split('T')[0],
        amount: 0,
        note: '',
      });
    } catch (e: any) {
      setError(getErrorTranslation(e, t));
    }
  };

  const handleRemovePayment = (billId: string, paymentIndex: number) => {
    showConfirm(t('common.delete'), t('bills.confirmRemovePayment'), async () => {
      try {
        await billService.removePayment(billId, paymentIndex);
        await loadBills(0, true);
        if (editingBill && editingBill.id === billId) {
          // Reload the bill being edited
          const updatedBill = await billService.getBillById(billId);
          setEditingBill(updatedBill);
          setFormData({
            amount: updatedBill.amount,
            resourceType: updatedBill.resourceType,
            resourceId: updatedBill.resourceId,
            payments: updatedBill.payments || [],
          });
        }
        if (viewingBill && viewingBill.id === billId) {
          const updatedBill = await billService.getBillById(billId);
          setViewingBill(updatedBill);
        }
      } catch (e: any) {
        setError(getErrorTranslation(e, t));
        showAlert(t('common.error'), getErrorTranslation(e, t));
      }
    });
  };

  const handleViewBill = async (bill: Bill) => {
    try {
      const fullBill = await billService.getBillById(bill.id);
      setViewingBill(fullBill);
    } catch (e: any) {
      setError(getErrorTranslation(e, t));
    }
  };

  const handleCloseViewModal = () => {
    setViewingBill(null);
    setPaymentModalOpen(false);
    setEditingPaymentIndex(null);
    setNewPayment({
      paymentDate: new Date().toISOString().split('T')[0],
      amount: 0,
      note: '',
    });
  };

  const handleOpenPaymentModal = (bill: Bill, payment?: Payment, index?: number) => {
    setViewingBill(bill);
    setPaymentModalOpen(true);
    if (payment && index !== undefined) {
      setEditingPaymentIndex(index);
      setNewPayment({
        ...payment,
        paymentDate: new Date(payment.paymentDate).toISOString().split('T')[0],
        amount: payment.amount,
        note: payment.note || '',
      });
    } else {
      setEditingPaymentIndex(null);
      setNewPayment({
        paymentDate: new Date().toISOString().split('T')[0],
        amount: 0,
        note: '',
      });
    }
  };

  const getResourceOptions = () => {
    switch (formData.resourceType) {
      case ResourceType.STUDENT:
        return students.map(s => ({ id: s.id, name: `${s.firstName} ${s.lastName}` }));
      case ResourceType.TEACHER:
        return teachers.map(t => ({ id: t.id, name: `${t.firstName} ${t.lastName}` }));
      case ResourceType.ADMIN:
        return admins.map(a => ({ id: a.id, name: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.userName }));
      default:
        return [];
    }
  };

  const getResourceName = (bill: Bill): string => {
    return bill.resourceName || bill.resourceId;
  };

  if (loading && bills.length === 0) {
    return <div className="bo-container">{t('common.loading')}</div>;
  }

  return (
    <div className="bo-container">
      <div className="bo-header">
        <div className="header-left">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="btn-toggle-sidebar"
            title={sidebarOpen ? t('common.hideFilters') : t('common.showFilters')}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
          <h2>{t('bills.title')}</h2>
        </div>
        {hasRight('bills:c') && (
          <button className="btn-create" onClick={handleCreate}>
            {t('bills.createBill')}
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="bo-content-wrapper">
        {sidebarOpen && (
          <div className="bo-sidebar">
            <div className="sidebar-header">
              <h3>{t('bills.filters')}</h3>
              <div className="sidebar-header-actions">
                <button className="btn-deep-search" onClick={handleSearch}>
                  {t('common.search')}
                </button>
                <button className="btn-clear-filters" onClick={handleClearFilters}>
                  {t('common.clear')}
                </button>
              </div>
            </div>
            <div className="sidebar-body">
              <div className="filter-group">
                <label>{t('bills.status')}</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as BillStatus | '')}
                >
                  <option value="">{t('bills.allStatuses')}</option>
                  {Object.values(BillStatus).map(status => (
                    <option key={status} value={status}>{t(`bills.status.${status}`)}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>{t('bills.billType')}</label>
                <select
                  value={billTypeFilter}
                  onChange={(e) => setBillTypeFilter(e.target.value as BillType | '')}
                >
                  <option value="">{t('bills.allBillTypes')}</option>
                  {Object.values(BillType).map(type => (
                    <option key={type} value={type}>{t(`bills.billType.${type}`)}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>{t('bills.resourceType')}</label>
                <select
                  value={resourceTypeFilter}
                  onChange={(e) => {
                    setResourceTypeFilter(e.target.value as ResourceType | '');
                    setResourceIdFilter('');
                  }}
                >
                  <option value="">{t('bills.allResourceTypes')}</option>
                  {Object.values(ResourceType).map(type => (
                    <option key={type} value={type}>{t(`bills.resourceType.${type}`)}</option>
                  ))}
                </select>
              </div>

              {/* Class Filter (only if Student or All) */}
              {(!resourceTypeFilter || resourceTypeFilter === ResourceType.STUDENT) && (
                <div className="filter-group">
                  <label>{t('bills.class')}</label>
                  <select
                    value={classFilter}
                    onChange={(e) => setClassFilter(e.target.value)}
                  >
                    <option value="">{t('bills.allClasses')}</option>
                    {classes.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Name Search */}
              <div className="filter-group">
                <label>{t('bills.resourceName')}</label>
                <input
                  type="text"
                  value={resourceNameFilter}
                  onChange={(e) => setResourceNameFilter(e.target.value)}
                  placeholder={t('bills.searchNamePlaceholder')}
                />
              </div>

              <div className="filter-group">
                <label>{t('bills.fromDate')}</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>{t('bills.toDate')}</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        <div className={`bo-main-content ${!sidebarOpen ? 'full-width' : ''}`}>

          {/* Create/Edit Bill Modal */}
          {(isCreating || editingBill) && (
            <div className="modal-overlay" onClick={handleCancel}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editingBill ? t('bills.editBill') : t('bills.createBill')}</h3>
                  <button className="btn-close-modal" onClick={handleCancel}>×</button>
                </div>
                <div className="modal-body">
                  {!editingBill && (
                    <div className="form-group">
                      <label style={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>

                        <input
                          type="checkbox"
                          checked={isBulkMode}
                          onChange={(e) => {
                            setIsBulkMode(e.target.checked);
                            setSelectedResourceIds([]);
                            setSelectedClassId('');
                            setResourceSearchTerm('');
                            setFormData({ ...formData, resourceId: '' });
                          }}
                        />
                        {t('bills.bulkMode')}
                      </label>
                    </div>
                  )}

                  <div className="form-group">
                    <label>{t('bills.resourceType')} *</label>
                    <select
                      value={formData.resourceType}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          resourceType: e.target.value as ResourceType,
                          resourceId: '',
                        });
                        setSelectedResourceIds([]);
                        setSelectedClassId('');
                        loadResources();
                      }}
                    >
                      {Object.values(ResourceType).map(type => (
                        <option key={type} value={type}>{t(`bills.resourceType.${type}`)}</option>
                      ))}
                    </select>
                    <small className="form-hint">
                      {formData.resourceType === ResourceType.STUDENT
                        ? t('bills.billTypeHint.INCOME')
                        : t('bills.billTypeHint.OUTCOME')}
                    </small>
                  </div>

                  {isBulkMode && formData.resourceType === ResourceType.STUDENT && (
                    <div className="form-group">
                      <label>{t('bills.selectClass')} *</label>
                      <select
                        value={selectedClassId}
                        onChange={(e) => {
                          setSelectedClassId(e.target.value);
                          setSelectedResourceIds([]);
                        }}
                      >
                        <option value="">{t('bills.selectClass')}</option>
                        {classes.map(classe => (
                          <option key={classe.id} value={classe.id}>{classe.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label>{t('bills.amount')} *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                    />
                  </div>

                  {!editingBill && (
                    <div className="form-group">
                      <label>{t('bills.creationDate')} *</label>
                      <input
                        type="datetime-local"
                        value={toLocalISOString(formData.creationDatetime || '')}
                        onChange={(e) => {
                          const dateValue = e.target.value; // "YYYY-MM-DDTHH:mm" (local)
                          if (dateValue) {
                            const dateObj = new Date(dateValue);
                            setFormData({ ...formData, creationDatetime: dateObj.toISOString() });
                          }
                        }}
                        disabled={editingBill !== null}
                      />
                      <small className="form-hint">{t('bills.creationDateHint')}</small>
                    </div>
                  )}

                  {isBulkMode ? (
                    <div className="form-group">
                      <div className="multiselect-header">
                        <label>{t('bills.selectResources')} * ({selectedResourceIds.length} {t('bills.selected')})</label>
                        <div className="multiselect-actions">
                          <button
                            type="button"
                            className="btn-check-all"
                            onClick={() => {
                              const allOptions = formData.resourceType === ResourceType.STUDENT
                                ? (selectedClassId ? classStudents : students).map(s => ({ id: s.id, name: `${s.firstName} ${s.lastName}` }))
                                : formData.resourceType === ResourceType.TEACHER
                                  ? teachers.map(t => ({ id: t.id, name: `${t.firstName} ${t.lastName}` }))
                                  : admins.map(a => ({ id: a.id, name: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.userName }));

                              // Filter by search term if present
                              let filteredOptions = allOptions;
                              if (resourceSearchTerm.trim()) {
                                const searchLower = resourceSearchTerm.toLowerCase();
                                filteredOptions = allOptions.filter(opt => opt.name.toLowerCase().includes(searchLower));
                              }

                              // Add all filtered options that aren't already selected
                              setSelectedResourceIds(prev => {
                                const newIds = [...prev];
                                filteredOptions.forEach(opt => {
                                  if (!newIds.includes(opt.id)) {
                                    newIds.push(opt.id);
                                  }
                                });
                                return newIds;
                              });
                            }}
                          >
                            {t('bills.checkAll')}
                          </button>
                          <button
                            type="button"
                            className="btn-uncheck-all"
                            onClick={() => {
                              if (resourceSearchTerm.trim()) {
                                // Only uncheck filtered items
                                const searchLower = resourceSearchTerm.toLowerCase();
                                const allOptions = formData.resourceType === ResourceType.STUDENT
                                  ? (selectedClassId ? classStudents : students).map(s => ({ id: s.id, name: `${s.firstName} ${s.lastName}` }))
                                  : formData.resourceType === ResourceType.TEACHER
                                    ? teachers.map(t => ({ id: t.id, name: `${t.firstName} ${t.lastName}` }))
                                    : admins.map(a => ({ id: a.id, name: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.userName }));
                                const filteredIds = allOptions
                                  .filter(opt => opt.name.toLowerCase().includes(searchLower))
                                  .map(opt => opt.id);
                                setSelectedResourceIds(prev => prev.filter(id => !filteredIds.includes(id)));
                              } else {
                                // Uncheck all
                                setSelectedResourceIds([]);
                              }
                            }}
                          >
                            {t('bills.uncheckAll')}
                          </button>
                        </div>
                      </div>
                      <div className="multiselect-container">
                        <input
                          type="text"
                          className="multiselect-search"
                          placeholder={t('bills.searchResources')}
                          value={resourceSearchTerm}
                          onChange={(e) => setResourceSearchTerm(e.target.value)}
                        />
                        <div className="multiselect-options">
                          {(() => {
                            let options = formData.resourceType === ResourceType.STUDENT
                              ? (selectedClassId ? classStudents : students).map(s => ({ id: s.id, name: `${s.firstName} ${s.lastName}` }))
                              : formData.resourceType === ResourceType.TEACHER
                                ? teachers.map(t => ({ id: t.id, name: `${t.firstName} ${t.lastName}` }))
                                : admins.map(a => ({ id: a.id, name: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.userName }));

                            // Filter by search term
                            if (resourceSearchTerm.trim()) {
                              const searchLower = resourceSearchTerm.toLowerCase();
                              options = options.filter(opt => opt.name.toLowerCase().includes(searchLower));
                            }

                            return options.map(opt => (
                              <label key={opt.id} className="multiselect-option">
                                <input
                                  type="checkbox"
                                  checked={selectedResourceIds.includes(opt.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedResourceIds([...selectedResourceIds, opt.id]);
                                    } else {
                                      setSelectedResourceIds(selectedResourceIds.filter(id => id !== opt.id));
                                    }
                                  }}
                                />
                                {opt.name}
                              </label>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>{t('bills.resource')} *</label>
                      <select
                        value={formData.resourceId}
                        onChange={(e) => setFormData({ ...formData, resourceId: e.target.value })}
                      >
                        <option value="">{t('bills.selectResource')}</option>
                        {getResourceOptions().map(opt => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Initial Payment (only when creating) */}
                  {!editingBill && !isBulkMode && (
                    <div className="form-group">
                      <label style={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.payments && formData.payments.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                payments: [{
                                  paymentDate: new Date().toISOString().split('T')[0],
                                  amount: formData.amount, // Default to full amount
                                  note: t('bills.initialPayment')
                                }]
                              });
                            } else {
                              setFormData({ ...formData, payments: [] });
                            }
                          }}
                        />
                        {t('bills.addInitialPayment')}
                      </label>

                      {formData.payments && formData.payments.length > 0 && (
                        <div className="initial-payment-fields" style={{ marginLeft: '24px', marginTop: '8px', padding: '12px', background: '#f9f9f9', borderRadius: '4px' }}>
                          <div className="form-group">
                            <label>{t('bills.paymentDate')} *</label>
                            <input
                              type="date"
                              value={formData.payments[0].paymentDate}
                              onChange={(e) => {
                                const newPayments = [...(formData.payments || [])];
                                if (newPayments.length > 0) {
                                  newPayments[0] = { ...newPayments[0], paymentDate: e.target.value };
                                  setFormData({ ...formData, payments: newPayments });
                                }
                              }}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('bills.paymentAmount')} *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={formData.payments[0].amount}
                              onChange={(e) => {
                                const newPayments = [...(formData.payments || [])];
                                if (newPayments.length > 0) {
                                  newPayments[0] = { ...newPayments[0], amount: parseFloat(e.target.value) || 0 };
                                  setFormData({ ...formData, payments: newPayments });
                                }
                              }}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('bills.paymentNote')}</label>
                            <input
                              type="text"
                              value={formData.payments[0].note || ''}
                              onChange={(e) => {
                                const newPayments = [...(formData.payments || [])];
                                if (newPayments.length > 0) {
                                  newPayments[0] = { ...newPayments[0], note: e.target.value };
                                  setFormData({ ...formData, payments: newPayments });
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment Management (only when editing) */}
                  {editingBill && (
                    <div className="form-group">
                      <label>{t('bills.payments')}</label>
                      <div className="payments-list">
                        {editingBill.payments && editingBill.payments.length > 0 ? (
                          <table className="payments-table">
                            <thead>
                              <tr>
                                <th>{t('bills.paymentDate')}</th>
                                <th>{t('bills.paymentAmount')}</th>
                                <th>{t('bills.paymentNote')}</th>
                                <th>{t('common.actions')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {editingBill.payments.map((payment, index) => (
                                <tr key={index}>
                                  <td>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                                  <td>{payment.amount.toFixed(2)}</td>
                                  <td>{payment.note || '-'}</td>
                                  <td>
                                    <button
                                      className="btn-edit-small"
                                      onClick={() => handleOpenPaymentModal(editingBill, payment, index)}
                                      title={t('common.edit')}
                                    >
                                      <Pencil size={16} />
                                    </button>
                                    <button
                                      className="btn-delete-small"
                                      onClick={() => handleRemovePayment(editingBill.id, index)}
                                      title={t('common.delete')}
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="no-payments">{t('bills.noPayments')}</p>
                        )}
                        <button
                          className="btn-add-payment"
                          onClick={() => handleOpenPaymentModal(editingBill)}
                        >
                          {t('bills.addPayment')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn-save" onClick={handleSave}>{t('common.save')}</button>
                  <button className="btn-cancel" onClick={handleCancel}>{t('common.cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {/* View Bill Modal */}
          {viewingBill && (
            <div className="modal-overlay" onClick={handleCloseViewModal}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{t('bills.billDetails')}</h3>
                  <button className="btn-close-modal" onClick={handleCloseViewModal}>×</button>
                </div>
                <div className="modal-body">
                  <div className="bill-details">
                    <div className="detail-row">
                      <label>{t('bills.billType')}:</label>
                      <span>{t(`bills.billType.${viewingBill.billType}`)}</span>
                    </div>
                    <div className="detail-row">
                      <label>{t('bills.status')}:</label>
                      <span className={`status-badge status-${viewingBill.status.toLowerCase()}`}>
                        {t(`bills.status.${viewingBill.status}`)}
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>{t('bills.amount')}:</label>
                      <span>{viewingBill.amount.toFixed(2)}</span>
                    </div>
                    <div className="detail-row">
                      <label>{t('bills.resourceType')}:</label>
                      <span>{t(`bills.resourceType.${viewingBill.resourceType}`)}</span>
                    </div>
                    <div className="detail-row">
                      <label>{t('bills.resource')}:</label>
                      <span>{getResourceName(viewingBill)}</span>
                    </div>
                    <div className="detail-row">
                      <label>{t('bills.totalPaid')}:</label>
                      <span>{(viewingBill.totalPaid || 0).toFixed(2)}</span>
                    </div>
                    <div className="detail-row">
                      <label>{t('bills.created')}:</label>
                      <span>{viewingBill.creationDatetime ? new Date(viewingBill.creationDatetime).toLocaleString() : '-'}</span>
                    </div>
                  </div>

                  <div className="payments-section">
                    <h4>{t('bills.payments')} ({viewingBill.payments?.length || 0})</h4>
                    {viewingBill.payments && viewingBill.payments.length > 0 ? (
                      <table className="payments-table">
                        <thead>
                          <tr>
                            <th>{t('bills.paymentDate')}</th>
                            <th>{t('bills.paymentAmount')}</th>
                            <th>{t('bills.paymentNote')}</th>
                            {hasRight('bills:u') && <th>{t('common.actions')}</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {viewingBill.payments.map((payment, index) => (
                            <tr key={index}>
                              <td>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                              <td>{payment.amount.toFixed(2)}</td>
                              <td>{payment.note || '-'}</td>
                              {hasRight('bills:u') && (
                                <td>
                                  <button
                                    className="btn-edit-small"
                                    onClick={() => handleOpenPaymentModal(viewingBill, payment, index)}
                                    title={t('common.edit')}
                                  >
                                    <Pencil size={16} />
                                  </button>
                                  <button
                                    className="btn-delete-small"
                                    onClick={() => handleRemovePayment(viewingBill.id, index)}
                                    title={t('common.delete')}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="no-payments">{t('bills.noPayments')}</p>
                    )}
                    {hasRight('bills:u') && (
                      <button
                        className="btn-add-payment"
                        onClick={() => handleOpenPaymentModal(viewingBill)}
                      >
                        {t('bills.addPayment')}
                      </button>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-cancel" onClick={handleCloseViewModal}>{t('common.close')}</button>
                </div>
              </div>
            </div>
          )}

          {/* Payment Modal */}
          {paymentModalOpen && viewingBill && (
            <div className="modal-overlay" onClick={() => setPaymentModalOpen(false)}>
              <div className="modal-content payment-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editingPaymentIndex !== null ? t('common.edit') : t('bills.addPayment')}</h3>
                  <button className="btn-close-modal" onClick={() => setPaymentModalOpen(false)}>×</button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>{t('bills.paymentDate')} *</label>
                    <input
                      type="date"
                      value={newPayment.paymentDate}
                      onChange={(e) => setNewPayment({ ...newPayment, paymentDate: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('bills.paymentAmount')} *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newPayment.amount}
                      onChange={(e) => setNewPayment({ ...newPayment, amount: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('bills.paymentNote')}</label>
                    <textarea
                      value={newPayment.note || ''}
                      onChange={(e) => setNewPayment({ ...newPayment, note: e.target.value })}
                      rows={3}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn-save"
                    onClick={() => handleAddPayment(viewingBill.id, newPayment)}
                    disabled={newPayment.amount <= 0}
                  >
                    {t('common.save')}
                  </button>
                  <button className="btn-cancel" onClick={() => setPaymentModalOpen(false)}>
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bills Table */}
          <div className="bo-table-container">
            <div className="bo-table-wrapper" ref={observerTarget}>
              <table className="bo-table">
                <thead>
                  <tr>
                    <th>{t('bills.billType')}</th>
                    <th>{t('bills.status')}</th>
                    <th>{t('bills.amount')}</th>
                    <th>{t('bills.resourceType')}</th>
                    <th>{t('bills.totalPaid')}</th>
                    <th>{t('bills.payments')}</th>
                    <th>{t('bills.created')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="empty-state">{t('bills.noBills')}</td>
                    </tr>
                  ) : (
                    bills.map((bill) => (
                      <tr key={bill.id}>
                        <td>{t(`bills.billType.${bill.billType}`)}</td>
                        <td><span className={`status-badge status-${bill.status.toLowerCase()}`}>
                          {t(`bills.status.${bill.status}`)}
                        </span></td>
                        <td>{bill.amount.toFixed(2)}</td>
                        <td>{t(`bills.resourceType.${bill.resourceType}`)}</td>
                        <td>{(bill.totalPaid || 0).toFixed(2)}</td>
                        <td>{bill.payments?.length || 0}</td>
                        <td>{bill.creationDatetime ? new Date(bill.creationDatetime).toLocaleDateString() : '-'}</td>
                        <td>
                          <TableActions actions={[
                            ...(hasRight('bills:u') ? [{
                              label: t('common.edit'),
                              onClick: () => handleEdit(bill),
                              className: 'btn-edit',
                              icon: <Pencil size={16} />,
                              hideLabelDesktop: true
                            }] : []),
                            ...(hasRight('bills:d') ? [{
                              label: t('common.delete'),
                              onClick: () => handleDelete(bill),
                              className: 'btn-delete',
                              icon: <Trash2 size={16} />,
                              hideLabelDesktop: true,
                              variant: 'danger' as const
                            }] : []),
                            {
                              label: t('common.view'),
                              onClick: () => handleViewBill(bill),
                              className: 'btn-view',
                              icon: <Eye size={16} />,
                              hideLabelDesktop: true
                            }
                          ]} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="scroll-trigger">
              {loadingMore && (
                <div className="loading-more">
                  <small>{t('common.loading')}...</small>
                </div>
              )}
              {!hasMore && bills.length > 0 && (
                <div className="no-more-data">
                  <small>{t('bills.allLoaded')}</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bills;
